(() => {
  "use strict";

  const ELEMENT_NAME = "ar-cart-page";
  const PENDING_FOCUS = new Map();

  if (window.customElements.get(ELEMENT_NAME)) {
    return;
  }

  const formatQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return "01";
    }

    return parsed >= 0 && parsed < 10 ? String(parsed).padStart(2, "0") : String(parsed);
  };

  const safeJson = async (response) => {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  };

  class SolleroCartPage extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeObserver = null;
      this.stickyResizeObserver = null;
      this.measureFrame = 0;
      this.stickyMeasureFrame = 0;
      this.stickyScrollFrame = 0;
      this.stickyResizeTimer = 0;
      this.stickyColumn = null;
      this.stickyHeight = 0;
      this.stickyInitialTop = 0;
      this.stickyCurrentTop = 0;
      this.stickyLastScrollY = window.scrollY;
      this.stickyDesktopQuery = window.matchMedia("(min-width: 1024px)");
      this.refreshPromise = null;
      this.sectionId = "";

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPrepareBundledSections = this.onPrepareBundledSections.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      this.onCartRefresh = this.onCartRefresh.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      if (this.abortController) {
        return;
      }

      this.sectionId = this.dataset.sectionId || "";
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener("click", this.onClick, { signal });
      this.addEventListener("change", this.onChange, { signal });
      this.addEventListener("input", this.onInput, { signal });
      this.addEventListener("keydown", this.onKeyDown, { signal });
      this.addEventListener("submit", this.onSubmit, { signal });

      document.addEventListener("cart:prepare-bundled-sections", this.onPrepareBundledSections, { signal });
      document.addEventListener("cart:change", this.onCartChange, { signal });
      document.addEventListener("cart:refresh", this.onCartRefresh, { signal });
      window.addEventListener("pageshow", this.onPageShow, { signal });
      window.addEventListener("resize", () => {
        this.requestScrollableMeasure();
        this.requestStickyMeasure();
      }, { signal, passive: true });
      window.addEventListener("scroll", () => this.requestStickyScrollUpdate(), { signal, passive: true });

      this.querySelectorAll("[data-hz-cart-quantity-input]").forEach((input) => this.syncQuantityDisplay(input));
      this.setupScrollableMeasurement();
      this.setupStickyMeasurement();

      requestAnimationFrame(() => this.restorePendingFocus());
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.stickyResizeObserver?.disconnect();
      this.stickyResizeObserver = null;

      if (this.measureFrame) {
        cancelAnimationFrame(this.measureFrame);
        this.measureFrame = 0;
      }

      if (this.stickyMeasureFrame) {
        cancelAnimationFrame(this.stickyMeasureFrame);
        this.stickyMeasureFrame = 0;
      }

      if (this.stickyScrollFrame) {
        cancelAnimationFrame(this.stickyScrollFrame);
        this.stickyScrollFrame = 0;
      }

      window.clearTimeout(this.stickyResizeTimer);
      this.clearStickyState();
    }

    onPrepareBundledSections(event) {
      if (!this.sectionId || !Array.isArray(event.detail?.sections)) {
        return;
      }

      if (!event.detail.sections.includes(this.sectionId)) {
        event.detail.sections.push(this.sectionId);
      }
    }

    onCartChange(event) {
      const html = event.detail?.cart?.sections?.[this.sectionId];

      if (typeof html === "string" && html.trim() !== "" && this.replaceFromSectionHtml(html)) {
        return;
      }

      this.refreshSection();
    }

    onCartRefresh(event) {
      if (event.detail?.source === "hz-cart-page") {
        return;
      }

      this.refreshSection();
    }

    onPageShow(event) {
      if (event.persisted) {
        this.refreshSection();
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const discountRemove = event.target.closest("[data-hz-cart-page-discount-remove]");
      if (discountRemove) {
        event.preventDefault();

        if (this.isBusy()) {
          return;
        }

        const code = discountRemove.getAttribute("data-discount-code")?.trim();
        if (code) {
          this.removeDiscountCode(code, discountRemove);
        }
        return;
      }

      const discountApply = event.target.closest("[data-hz-cart-page-discount-apply]");
      if (discountApply) {
        event.preventDefault();

        if (!this.isBusy()) {
          this.applyDiscountCode(discountApply);
        }
        return;
      }

      const lineAction = event.target.closest("[data-hz-cart-page-line-action]");
      if (!lineAction) {
        return;
      }

      if (this.isBusy()) {
        event.preventDefault();
        return;
      }

      const item = lineAction.closest("[data-hz-cart-item]");
      if (!item) {
        return;
      }

      const href = lineAction.getAttribute("href");
      if (!href) {
        return;
      }

      const url = new URL(href, window.location.href);
      const lineKey = url.searchParams.get("id") || item.dataset.hzCartLineKey || "";
      const targetQuantity = Number.parseInt(url.searchParams.get("quantity"), 10);

      if (!lineKey || !Number.isFinite(targetQuantity)) {
        return;
      }

      event.preventDefault();
      const kind = lineAction.getAttribute("data-hz-cart-page-line-action") || "input";
      const focus = this.createFocusRecord(item, kind, lineKey);
      this.changeLineQuantity(lineKey, targetQuantity, item, focus);
    }

    onChange(event) {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }

      const input = event.target.closest("[data-hz-cart-quantity-input]");
      if (!input || this.isBusy()) {
        return;
      }

      const item = input.closest("[data-hz-cart-item]");
      const lineKey = input.getAttribute("data-line-key") || item?.dataset.hzCartLineKey || "";
      const targetQuantity = Number.parseInt(input.value, 10);

      if (!item || !lineKey || !Number.isFinite(targetQuantity)) {
        return;
      }

      const focus = this.createFocusRecord(item, "input", lineKey);
      this.changeLineQuantity(lineKey, targetQuantity, item, focus);
    }

    onInput(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const quantityInput = event.target.closest("[data-hz-cart-quantity-input]");
      if (quantityInput) {
        this.syncQuantityDisplay(quantityInput);
      }

      if (event.target.closest("[data-hz-cart-page-discount-input]")) {
        this.hideDiscountError();
      }
    }

    onKeyDown(event) {
      if (!(event.target instanceof Element) || event.key !== "Enter") {
        return;
      }

      const shippingEstimator = event.target.closest("shipping-estimator");
      if (shippingEstimator && event.target.matches('[name="address[zip]"]')) {
        event.preventDefault();
        shippingEstimator.querySelector('[type="button"]')?.click();
        return;
      }

      const discountInput = event.target.closest("[data-hz-cart-page-discount-input]");
      if (discountInput) {
        event.preventDefault();
        if (!this.isBusy()) {
          this.applyDiscountCode(this.querySelector("[data-hz-cart-page-discount-apply]"));
        }
        return;
      }

      const quantityInput = event.target.closest("[data-hz-cart-quantity-input]");
      if (quantityInput) {
        event.preventDefault();
        quantityInput.blur();
      }
    }

    onSubmit(event) {
      if (this.isBusy()) {
        event.preventDefault();
        return;
      }

      const active = document.activeElement;
      if (active instanceof Element && active.matches("[data-hz-cart-page-discount-input], [data-hz-cart-quantity-input]")) {
        event.preventDefault();
      }
    }

    createFocusRecord(item, kind, lineKey) {
      const list = item.closest("[data-hz-cart-page-items]");
      const items = list ? Array.from(list.querySelectorAll(":scope > [data-hz-cart-item]")) : [];

      return {
        kind,
        lineKey,
        index: Math.max(0, items.indexOf(item)),
        listScrollTop: list?.scrollTop || 0
      };
    }

    syncQuantityDisplay(input) {
      const valueWrap = input.closest(".hz__cartItemQuantityValue");
      const display = valueWrap?.querySelector("[data-hz-cart-quantity-display]");

      if (display) {
        display.textContent = formatQuantity(input.value);
      }
    }

    async changeLineQuantity(lineKey, targetQuantity, item, focusRecord) {
      this.hideLineError(item);
      item?.dispatchEvent(
        new CustomEvent("line-item:will-change", {
          bubbles: true,
          detail: { targetQuantity }
        })
      );
      this.setBusy(true, item);
      PENDING_FOCUS.set(this.sectionId, focusRecord);
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      try {
        const sections = this.collectBundledSections();
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            id: lineKey,
            quantity: targetQuantity,
            sections: sections.join(","),
            sections_url: this.getSectionsUrl()
          })
        });

        const cart = await safeJson(response);

        if (!response.ok || !cart) {
          PENDING_FOCUS.delete(this.sectionId);
          const message = cart?.description || cart?.message || this.dataset.genericErrorMessage;
          this.showLineError(item, message);
          this.restoreInputDefault(item);
          this.setBusy(false, item);
          return;
        }

        const lineAfter = Array.isArray(cart.items) ? cart.items.find((entry) => entry.key === lineKey) : null;
        item.dispatchEvent(
          new CustomEvent("line-item:change", {
            bubbles: true,
            detail: {
              quantity: lineAfter ? lineAfter.quantity : 0,
              cart
            }
          })
        );

        await this.propagateCartResult(cart, "line-item:change");
      } catch (_error) {
        PENDING_FOCUS.delete(this.sectionId);
        this.showLineError(item, this.dataset.genericErrorMessage);
        this.restoreInputDefault(item);
        this.setBusy(false, item);
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }

    async applyDiscountCode(button) {
      const input = this.querySelector("[data-hz-cart-page-discount-input]");
      const code = input?.value.trim();

      if (!input || !code) {
        this.showDiscountError();
        input?.focus({ preventScroll: true });
        return;
      }

      const currentCodes = this.getAppliedDiscountCodes();
      const normalized = code.toLowerCase();

      if (!currentCodes.some((entry) => entry.toLowerCase() === normalized)) {
        currentCodes.push(code);
      }

      const focus = {
        kind: "discount-apply",
        lineKey: "",
        index: 0,
        listScrollTop: this.querySelector("[data-hz-cart-page-items]")?.scrollTop || 0
      };

      await this.updateDiscountCodes(currentCodes, button, code, focus);
    }

    async removeDiscountCode(code, button) {
      const currentCodes = this.getAppliedDiscountCodes();
      const normalized = code.toLowerCase();
      const nextCodes = currentCodes.filter((entry) => entry.toLowerCase() !== normalized);
      const focus = {
        kind: "discount-remove",
        discountCode: code,
        lineKey: "",
        index: 0,
        listScrollTop: this.querySelector("[data-hz-cart-page-items]")?.scrollTop || 0
      };

      await this.updateDiscountCodes(nextCodes, button, null, focus);
    }

    async updateDiscountCodes(codes, button, codeBeingApplied, focusRecord) {
      this.hideDiscountError();
      this.setBusy(true);
      button?.setAttribute("aria-busy", "true");
      PENDING_FOCUS.set(this.sectionId, focusRecord);
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      try {
        const sections = this.collectBundledSections();
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            discount: codes.join(","),
            sections: sections.join(","),
            sections_url: this.getSectionsUrl()
          })
        });

        const cart = await safeJson(response);

        if (!response.ok || !cart) {
          PENDING_FOCUS.delete(this.sectionId);
          this.setBusy(false);
          button?.removeAttribute("aria-busy");
          this.showDiscountError();
          return;
        }

        if (codeBeingApplied && Array.isArray(cart.discount_codes)) {
          const requested = cart.discount_codes.find((entry) => String(entry.code || "").toLowerCase() === codeBeingApplied.toLowerCase());
          if (!requested || requested.applicable === false) {
            PENDING_FOCUS.delete(this.sectionId);
            this.setBusy(false);
            button?.removeAttribute("aria-busy");
            this.showDiscountError();
            return;
          }
        }

        await this.propagateCartResult(cart, "cart:discount");
      } catch (_error) {
        PENDING_FOCUS.delete(this.sectionId);
        this.setBusy(false);
        button?.removeAttribute("aria-busy");
        this.showDiscountError();
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }

    getAppliedDiscountCodes() {
      const codes = [];
      const seen = new Set();

      this.querySelectorAll("[data-hz-cart-page-discount-code]").forEach((element) => {
        const code = element.getAttribute("data-discount-code")?.trim();
        const normalized = code?.toLowerCase();

        if (code && normalized && !seen.has(normalized)) {
          seen.add(normalized);
          codes.push(code);
        }
      });

      return codes;
    }

    collectBundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections }
        })
      );

      const uniqueSections = Array.from(new Set(sections.filter(Boolean)));

      if (this.sectionId) {
        const ownIndex = uniqueSections.indexOf(this.sectionId);
        if (ownIndex !== -1) {
          uniqueSections.splice(ownIndex, 1);
        }
        uniqueSections.unshift(this.sectionId);
      }

      // Shopify bundled section rendering accepts at most five section IDs.
      return uniqueSections.slice(0, 5);
    }

    getSectionsUrl() {
      return `${window.location.pathname}${window.location.search}` || "/cart";
    }

    async propagateCartResult(cart, baseEvent) {
      const renderedSections = cart?.sections;
      const pageHtml = renderedSections?.[this.sectionId];
      const hasFailedBundledSection = renderedSections && Object.values(renderedSections).some((html) => html == null);

      if (typeof pageHtml === "string" && pageHtml.trim() !== "" && !hasFailedBundledSection) {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent, cart }
          })
        );
        return;
      }

      if (typeof pageHtml === "string" && pageHtml.trim() !== "") {
        this.replaceFromSectionHtml(pageHtml);
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: { source: "hz-cart-page" }
          })
        );
        return;
      }

      const refreshed = await this.refreshSection();
      if (!refreshed) {
        PENDING_FOCUS.delete(this.sectionId);
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: { source: "hz-cart-page" }
        })
      );
    }

    setBusy(isBusy, item = null) {
      if (isBusy) {
        this.setAttribute("aria-busy", "true");
      } else {
        this.removeAttribute("aria-busy");
      }

      if (item) {
        if (isBusy) {
          item.setAttribute("aria-busy", "true");
        } else {
          item.removeAttribute("aria-busy");
        }
      }

      this.querySelectorAll("[data-hz-cart-page-checkout], [data-hz-cart-page-discount-apply]").forEach((control) => {
        if (control instanceof HTMLButtonElement) {
          control.disabled = isBusy;
        }
      });

      this.querySelectorAll("[data-hz-cart-page-accelerated-checkout]").forEach((checkout) => {
        checkout.toggleAttribute("inert", isBusy);
        checkout.setAttribute("aria-disabled", isBusy ? "true" : "false");
      });
    }

    isBusy() {
      return this.getAttribute("aria-busy") === "true";
    }

    restoreInputDefault(item) {
      const input = item?.querySelector("[data-hz-cart-quantity-input]");
      if (!input) {
        return;
      }

      input.value = input.defaultValue;
      this.syncQuantityDisplay(input);
    }

    showLineError(item, message) {
      const error = item?.querySelector("[data-hz-cart-page-line-error]");
      if (!error) {
        return;
      }

      error.textContent = message || this.dataset.genericErrorMessage || "Unable to update cart.";
      error.hidden = false;
    }

    hideLineError(item) {
      const error = item?.querySelector("[data-hz-cart-page-line-error]");
      if (error) {
        error.hidden = true;
        error.textContent = "";
      }
    }

    showDiscountError() {
      const error = this.querySelector("[data-hz-cart-page-discount-error]");
      if (!error) {
        return;
      }

      error.textContent = this.dataset.discountErrorMessage || "Enter a valid discount code";
      error.hidden = false;
      this.querySelector("[data-hz-cart-page-discount-input]")?.focus({ preventScroll: true });
    }

    hideDiscountError() {
      const error = this.querySelector("[data-hz-cart-page-discount-error]");
      if (error) {
        error.hidden = true;
      }
    }

    async refreshSection() {
      if (!this.sectionId || this.refreshPromise) {
        return this.refreshPromise;
      }

      const url = new URL(window.location.href);
      url.searchParams.set("section_id", this.sectionId);

      this.refreshPromise = fetch(url.toString(), { headers: { Accept: "text/html" } })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Cart section refresh failed");
          }
          return response.text();
        })
        .then((html) => this.replaceFromSectionHtml(html))
        .catch(() => {
          this.setBusy(false);
          return false;
        })
        .finally(() => {
          this.refreshPromise = null;
        });

      return this.refreshPromise;
    }

    replaceFromSectionHtml(html) {
      if (!this.isConnected || !this.sectionId || typeof html !== "string") {
        return false;
      }

      const parsed = new DOMParser().parseFromString(html, "text/html");
      const section = parsed.getElementById(`shopify-section-${this.sectionId}`);
      const sourceRoot = section?.querySelector("[data-hz-cart-page]");

      if (!sourceRoot) {
        this.setBusy(false);
        return false;
      }

      const fragment = document.createRange().createContextualFragment(sourceRoot.outerHTML);
      const nextRoot = fragment.querySelector("[data-hz-cart-page]");

      if (!nextRoot) {
        this.setBusy(false);
        return false;
      }

      this.replaceWith(nextRoot);
      return true;
    }

    setupStickyMeasurement() {
      if (!this.stickyEnabled) {
        this.clearStickyState();
        return;
      }

      const productsColumn = this.querySelector(".hz__cartPageProductsColumn");
      const summaryColumn = this.querySelector(".hz__cartPageSummary");

      if (!(productsColumn instanceof HTMLElement) || !(summaryColumn instanceof HTMLElement)) {
        this.clearStickyState();
        return;
      }

      if ("ResizeObserver" in window) {
        this.stickyResizeObserver = new ResizeObserver(() => {
          window.clearTimeout(this.stickyResizeTimer);
          this.stickyResizeTimer = window.setTimeout(() => this.requestStickyMeasure(), 80);
        });
        this.stickyResizeObserver.observe(productsColumn);
        this.stickyResizeObserver.observe(summaryColumn);
      }

      this.requestStickyMeasure();
    }

    get stickyEnabled() {
      return this.dataset.stickyEnabled === "true";
    }

    requestStickyMeasure() {
      if (!this.stickyEnabled || this.stickyMeasureFrame) {
        return;
      }

      this.stickyMeasureFrame = requestAnimationFrame(() => {
        this.stickyMeasureFrame = 0;
        this.measureStickyColumns();
      });
    }

    measureStickyColumns() {
      this.clearStickyState();

      if (!this.stickyEnabled || !this.stickyDesktopQuery.matches) {
        return;
      }

      const productsColumn = this.querySelector(".hz__cartPageProductsColumn");
      const summaryColumn = this.querySelector(".hz__cartPageSummary");
      const body = this.querySelector(".hz__cartPageBody");

      if (!(productsColumn instanceof HTMLElement) || !(summaryColumn instanceof HTMLElement) || !(body instanceof HTMLElement)) {
        return;
      }

      const productsHeight = this.measureColumnHeight(productsColumn);
      const summaryHeight = this.measureColumnHeight(summaryColumn);

      if (productsHeight <= 0 || summaryHeight <= 0 || Math.abs(productsHeight - summaryHeight) < 8) {
        return;
      }

      this.stickyColumn = productsHeight < summaryHeight ? productsColumn : summaryColumn;
      this.stickyColumn.classList.add("hz__cartPageColumn--sticky");
      this.stickyColumn.setAttribute("data-hz-cart-page-sticky-active", "");
      this.stickyColumn.dataset.hzCartPageStickyColumn = this.stickyColumn === productsColumn ? "products" : "summary";

      const configuredTop = Number.parseFloat(getComputedStyle(this).getPropertyValue("--hz-cart-page-sticky-offset"));
      this.stickyInitialTop = Number.isFinite(configuredTop) ? Math.max(0, configuredTop) : 24;
      this.stickyCurrentTop = this.stickyInitialTop;
      this.stickyHeight = this.measureColumnHeight(this.stickyColumn);
      this.stickyLastScrollY = window.scrollY;
      this.stickyColumn.style.setProperty("--hz-cart-page-column-sticky-top", `${Math.round(this.stickyInitialTop)}px`);
      this.updateStickyTop({ reset: true });
    }

    measureColumnHeight(column) {
      const rectHeight = column.getBoundingClientRect().height;
      return Math.max(rectHeight, column.scrollHeight, column.offsetHeight);
    }

    requestStickyScrollUpdate() {
      if (!this.stickyColumn || this.stickyScrollFrame) {
        return;
      }

      this.stickyScrollFrame = requestAnimationFrame(() => {
        this.stickyScrollFrame = 0;
        this.updateStickyTop();
      });
    }

    updateStickyTop({ reset = false } = {}) {
      const target = this.stickyColumn;
      if (!(target instanceof HTMLElement) || !this.stickyEnabled || !this.stickyDesktopQuery.matches) {
        return;
      }

      const targetHeight = this.stickyHeight || this.measureColumnHeight(target);
      const bottomGap = 20;
      const availableHeight = Math.max(0, window.innerHeight - this.stickyInitialTop - bottomGap);
      const nextScrollY = window.scrollY;

      if (targetHeight <= availableHeight) {
        this.stickyCurrentTop = this.stickyInitialTop;
        this.stickyLastScrollY = nextScrollY;
        target.removeAttribute("data-hz-cart-page-sticky-tall");
        target.style.setProperty("--hz-cart-page-column-sticky-top", `${Math.round(this.stickyCurrentTop)}px`);
        return;
      }

      target.setAttribute("data-hz-cart-page-sticky-tall", "");

      if (reset) {
        this.stickyCurrentTop = this.stickyInitialTop;
        this.stickyLastScrollY = nextScrollY;
        target.style.setProperty("--hz-cart-page-column-sticky-top", `${Math.round(this.stickyCurrentTop)}px`);
        return;
      }

      const body = this.querySelector(".hz__cartPageBody");
      const layoutRect = body?.getBoundingClientRect();
      const sectionIsEngaged = Boolean(layoutRect && layoutRect.top <= this.stickyInitialTop && layoutRect.bottom > bottomGap);
      const delta = nextScrollY - this.stickyLastScrollY;
      this.stickyLastScrollY = nextScrollY;

      if (!sectionIsEngaged) {
        this.stickyCurrentTop = this.stickyInitialTop;
      } else if (delta !== 0) {
        this.stickyCurrentTop -= delta;
      }

      const minimumTop = window.innerHeight - targetHeight - bottomGap;
      this.stickyCurrentTop = Math.min(this.stickyInitialTop, Math.max(minimumTop, this.stickyCurrentTop));
      target.style.setProperty("--hz-cart-page-column-sticky-top", `${Math.round(this.stickyCurrentTop)}px`);
    }

    clearStickyState() {
      this.querySelectorAll(".hz__cartPageProductsColumn, .hz__cartPageSummary").forEach((column) => {
        if (!(column instanceof HTMLElement)) {
          return;
        }

        column.classList.remove("hz__cartPageColumn--sticky");
        column.removeAttribute("data-hz-cart-page-sticky-active");
        column.removeAttribute("data-hz-cart-page-sticky-tall");
        delete column.dataset.hzCartPageStickyColumn;
        column.style.removeProperty("--hz-cart-page-column-sticky-top");
      });

      this.stickyColumn = null;
      this.stickyHeight = 0;
      this.stickyCurrentTop = 0;
      this.stickyInitialTop = 0;
    }

    setupScrollableMeasurement() {
      if (this.dataset.productsBehavior !== "scroll_items") {
        return;
      }

      const list = this.querySelector("[data-hz-cart-page-items]");
      if (!list) {
        return;
      }

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestScrollableMeasure());
        this.resizeObserver.observe(list);
        list.querySelectorAll(":scope > [data-hz-cart-item]").forEach((item) => this.resizeObserver.observe(item));
      }

      this.requestScrollableMeasure();
    }

    requestScrollableMeasure() {
      if (this.dataset.productsBehavior !== "scroll_items" || this.measureFrame) {
        return;
      }

      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measureScrollableItems();
      });
    }

    measureScrollableItems() {
      const list = this.querySelector("[data-hz-cart-page-items]");
      if (!list) {
        return;
      }

      const items = Array.from(list.querySelectorAll(":scope > [data-hz-cart-item]")).slice(0, 4);
      if (items.length === 0) {
        list.style.removeProperty("--hz-cart-page-items-max-height");
        return;
      }

      const styles = getComputedStyle(list);
      const gap = Number.parseFloat(styles.rowGap || styles.gap || "0") || 0;
      const height = items.reduce((total, item) => total + item.getBoundingClientRect().height, 0) + gap * Math.max(0, items.length - 1);

      if (height > 0) {
        list.style.setProperty("--hz-cart-page-items-max-height", `${Math.ceil(height)}px`);
        this.requestStickyMeasure();
      }
    }

    restorePendingFocus() {
      const pending = PENDING_FOCUS.get(this.sectionId);
      if (!pending) {
        return;
      }

      PENDING_FOCUS.delete(this.sectionId);

      const list = this.querySelector("[data-hz-cart-page-items]");
      if (list && Number.isFinite(pending.listScrollTop)) {
        list.scrollTop = pending.listScrollTop;
      }

      if (this.querySelector("[data-hz-cart-page-empty]")) {
        const emptyTarget = this.querySelector("[data-hz-cart-page-empty] .hz__cartPageEmptyButton, [data-hz-cart-page-empty]");
        if (emptyTarget instanceof HTMLElement) {
          emptyTarget.focus({ preventScroll: true });
        }
        return;
      }

      let target = null;

      if (pending.kind === "discount-apply") {
        target = this.querySelector("[data-hz-cart-page-discount-input]");
      } else if (pending.kind === "discount-remove") {
        target = this.querySelector(`[data-hz-cart-page-discount-remove][data-discount-code="${CSS.escape(pending.discountCode || "")}"]`);
        target ||= this.querySelector("[data-hz-cart-page-discount-input]");
      } else {
        const items = Array.from(this.querySelectorAll("[data-hz-cart-item]"));
        const sameItem = items.find((item) => item.dataset.hzCartLineKey === pending.lineKey);

        if (sameItem && pending.kind !== "remove") {
          if (pending.kind === "input") {
            target = sameItem.querySelector("[data-hz-cart-quantity-input]");
          } else {
            target = sameItem.querySelector(`[data-hz-cart-page-line-action="${pending.kind}"]`);
            target ||= sameItem.querySelector("[data-hz-cart-quantity-input]");
          }
        } else if (items.length > 0) {
          const fallbackItem = items[Math.min(pending.index || 0, items.length - 1)];
          target = fallbackItem.querySelector(".hz__cartItemTitle[href], [data-hz-cart-page-line-action=\"remove\"], [data-hz-cart-quantity-input]");
        }
      }

      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
        target.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      }
    }
  }

  window.customElements.define(ELEMENT_NAME, SolleroCartPage);
})();
